/* eslint-disable */
import { useEffect, useState } from 'react';
import { Autocomplete, TextField, Radio } from '@mui/material';
import processFlowServices from '../../../../Services/Analytics Services/process-flow.services';
import { notificationServices } from '../../../../Services';
import styles from './ProcessAuditStep2.module.scss';

const ProcessAuditStep2 = ({
	stores = [],
	permittedStores = [],
	selectedStoreId,
	selectedProcessId,
	setSelectedStoreId,
	setSelectedProcessId,
}) => {
	const [processList, setProcessList] = useState([]);
	const [loadingProcesses, setLoadingProcesses] = useState(false);

	const visibleStores = (() => {
		if (!permittedStores || permittedStores.length === 0) return stores ?? [];
		const allowed = new Set(permittedStores.map((s) => s.id));
		return (stores ?? []).filter((s) => allowed.has(s.id));
	})();

	useEffect(() => {
		let cancelled = false;
		if (!selectedStoreId) {
			setProcessList([]);
			if (selectedProcessId) {
				setSelectedProcessId(null);
			}
			return undefined;
		}
		(async () => {
			try {
				setLoadingProcesses(true);
				// Mirrors how ImagesFinder calls fetchNodes (ImagesFinder.js:688) — the
				// response shape is { statusCode, data: { nodes: [...] } }.
				const response = await processFlowServices.fetchNodes({
					params: { storeId: selectedStoreId },
				});
				if (cancelled) return;
				const nodes = response?.data?.nodes ?? [];
				setProcessList(nodes);
				if (selectedProcessId && !nodes.some((n) => n.id === selectedProcessId)) {
					setSelectedProcessId(null);
				}
			} catch (err) {
				if (!cancelled) {
					notificationServices.generateNotification({
						type: 'error',
						message: 'Failed to fetch processes for this store',
					});
					setProcessList([]);
				}
			} finally {
				if (!cancelled) setLoadingProcesses(false);
			}
		})();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedStoreId]);

	const selectedStore = visibleStores.find((s) => s.id === selectedStoreId) || null;

	return (
		<div className={styles.root}>
			<div className={styles.section}>
				<div className={styles.sectionLabel}>Select Store</div>
				<Autocomplete
					options={visibleStores}
					getOptionLabel={(s) => s?.name || ''}
					value={selectedStore}
					onChange={(_, newValue) => {
						setSelectedStoreId(newValue?.id ?? null);
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							placeholder='Search and select a store'
							size='small'
						/>
					)}
					isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
				/>
			</div>

			{selectedStoreId && (
				<div className={styles.section}>
					<div className={styles.sectionLabel}>
						Select Process
						{loadingProcesses ? ' (loading…)' : ''}
					</div>
					{processList.length === 0 && !loadingProcesses ? (
						<div className={styles.empty}>
							No processes defined for this store.
						</div>
					) : (
						<div className={styles.processList}>
							{processList.map((proc) => {
								const isSelected = proc.id === selectedProcessId;
								// Display follows the codebase convention used in POGeneralDetails:
								// `${data.heading} (${data.sectionName ?? '-'})`
								const heading = proc?.data?.heading || proc?.name || proc?.processName || `Process ${proc?.id}`;
								const sectionName = proc?.data?.sectionName ?? '-';
								return (
									// eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
									<div
										key={proc.id}
										className={`${styles.processCard}${isSelected ? ` ${styles.selected}` : ''}`}
										onClick={() => setSelectedProcessId(proc.id)}
									>
										<Radio
											checked={isSelected}
											size='small'
											readOnly
										/>
										<div className={"flex flex-row items-center gap-2"}>
											<div className={styles.processName}>
												{heading}
											</div>
											<div className={styles.processSubtitle}>
												Section: {sectionName}
											</div>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default ProcessAuditStep2;
